import User from '@/modules/user/models/User'
import { useUserStore } from '@/modules/user/stores/UserStore'
import Vue from 'vue'
import ApiClient from '@/util/ApiClient'
import { trackLogin, trackLogout } from '@/util/segment-tracking'
import { Pinia, Store, defineStore, getActivePinia } from 'pinia'
import RegisterRequest from '../models/RegisterRequest'
import { RegisterResponse } from '../models/RegisterResponse'
import { sanitizeRedirectPath } from '../utils/redirectPath'
import { TokenStorage } from './TokenStorage'
import { useWebSocketStore } from '@/modules/global/stores/WebSocketStore'
import { LAST_ACTIVITY_STORAGE_KEY } from '@/modules/auth/IdleTimer'
import { sanitizeSetupRedirectPath } from '@/modules/auth/utils/setup-flow'
import { clearStaticRenderCache } from '@/util/tiptap-static-render'

export type AuthState = {
  loginLoading: boolean
  logoutLoading: boolean
  loginOnce: boolean
  redirectPath: string | null
  // this promise can be awaited from everywhere. implicitly tracks if currently a refresh is running or not
  refreshTokenPromise: Promise<unknown>
}

const tokenStorage = new TokenStorage()
const AUTH_REDIRECT_PATH_STORAGE_KEY = 'notch.auth.redirectPath'

function getRedirectPathFromStorage(): string | null {
  try {
    return sessionStorage.getItem(AUTH_REDIRECT_PATH_STORAGE_KEY)
  } catch {
    return null
  }
}

/** Deduplicates concurrent checkSpaceToken (e.g. isLoggedIn + other callers) while URL still has spaceToken. */
let checkSpaceTokenInFlight: Promise<void> | null = null

/** Deduplicates concurrent initUser so a second navigation guard waits for fetchCurrentUser instead of bailing on loginOnce. */
let initUserInFlight: Promise<void> | null = null

/** Deduplicates concurrent refresh token requests. */
let refreshTokenInFlight: Promise<boolean> | null = null

/**
 * Deduplicates concurrent logouts. Latched from the first call, before `logoutLoading` is set, so work that
 * runs ahead of it (flushing pending saves) cannot start a second logout when one of its requests gets a 401.
 */
let logoutInFlight: Promise<void> | null = null

type ClearStoresHook = () => void
const clearStoresHooks = new Set<ClearStoresHook>()

/**
 * Runs `hook` whenever `clearAllStores()` resets every store (logout, switching to a space token). For state
 * kept outside Pinia, which `$reset()` cannot reach. Returns an unregister function.
 */
export function onClearAllStores(hook: ClearStoresHook): () => void {
  clearStoresHooks.add(hook)
  return () => clearStoresHooks.delete(hook)
}

function urlSearchParamsToQueryRecord(params: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {}
  params.forEach((value, key) => {
    out[key] = value
  })
  return out
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    loginLoading: false,
    logoutLoading: false,
    loginOnce: false,
    redirectPath: null,
    refreshTokenPromise: Promise.resolve()
  }),
  actions: {
    getToken() {
      return tokenStorage.tokens?.token
    },
    hasStoredSessionCredentials() {
      return tokenStorage.hasPersistedCredentials()
    },
    /** Read-then-clear: returns whether credentials existed before wiping both token sets. */
    discardOrphanedSessionCredentials(): boolean {
      const hadCredentials = this.hasStoredSessionCredentials()
      if (hadCredentials) {
        this.clear()
      }
      return hadCredentials
    },
    getTokenExpiry() {
      return tokenStorage.tokens?.tokenExpiry
    },
    async isLoggedIn(): Promise<boolean> {
      await this.checkSpaceToken()
      return Boolean(this.getToken())
    },
    startLogin() {
      this.loginLoading = true
      this.loginOnce = true
    },
    endLogin() {
      this.loginLoading = false
    },
    setRedirectPath(redirectPath: string | null) {
      this.redirectPath = redirectPath
      try {
        if (redirectPath) {
          sessionStorage.setItem(AUTH_REDIRECT_PATH_STORAGE_KEY, redirectPath)
        } else {
          sessionStorage.removeItem(AUTH_REDIRECT_PATH_STORAGE_KEY)
        }
      } catch {
        // sessionStorage may be unavailable in some environments
      }
    },
    getEffectiveRedirectPath(): string | null {
      return sanitizeSetupRedirectPath(this.redirectPath ?? getRedirectPathFromStorage())
    },
    /**
     * `logout()` leaves logoutLoading latched for the rest of the dead session, and `refreshToken()`
     * is a no-op while it is set. Storing a credential is what starts a new session — not
     * `startLogin()`, which register, invite, anonymous and space-token flows never call — so every
     * one of those paths would otherwise inherit a permanently dead token refresh.
     */
    saveToken(token: string, refreshToken: string) {
      tokenStorage.storeTokenPair(token, refreshToken)
      this.logoutLoading = false
    },
    savePublicToken(token: string, refreshToken: string) {
      tokenStorage.storeTokenPairPublic(token, refreshToken)
      tokenStorage.setUsePublicSpaceToken(true)
      this.logoutLoading = false
    },
    /**
     * Resolves to whether a usable access token is in storage afterwards. Callers branch on this
     * instead of on a rejection: every failure mode here is caught locally, so a thrown-error
     * contract would be silently unreachable (NS-401 infinite retry loop).
     */
    async refreshToken(): Promise<boolean> {
      if (this.logoutLoading) {
        return false
      }

      const refreshToken = tokenStorage.tokens?.refreshToken
      if (!refreshToken) {
        // Drop the orphaned access token too. Leaving it made the session look live, so every
        // retry re-sent the same dead credential and got another 401.
        this.clear()
        console.info('No refresh token stored')
        return false
      }

      try {
        const response = await ApiClient.post('/users/refreshtoken', { refreshToken })
        if (this.logoutLoading) {
          return false
        }
        if (response.data.token && response.data.refreshToken) {
          tokenStorage.storeTokenPairDependingOnSpaceTokenUsage(response.data.token, response.data.refreshToken)
          return true
        }
        this.clear()
        console.error('Invalid or missing tokens in the response')
        return false
      } catch (error: any) {
        this.clear()
        if (!error.response) {
          console.error('Network error')
        } else {
          console.error('Refresh token request failed', error.response.data)
        }
        return false
      }
    },
    clear() {
      tokenStorage.deleteTokens()
    },
    async checkSpaceToken(): Promise<void> {
      if (checkSpaceTokenInFlight) {
        await checkSpaceTokenInFlight
        return
      }

      checkSpaceTokenInFlight = (async () => {
        const query = new URLSearchParams(window.location.search)

        const spaceTokenNames = ['spaceToken', 'workspaceToken']
        const spaceTokenName = query.has(spaceTokenNames[0])
          ? spaceTokenNames[0]
          : query.has(spaceTokenNames[1])
          ? spaceTokenNames[1]
          : null
        if (!spaceTokenName) {
          return
        }

        const isPrintModeQuery = query.get('printMode') === 'true'

        const spaceToken = query.get(spaceTokenName)!
        const authorizationHeader = isPrintModeQuery ? `Bearer ${spaceToken}` : spaceToken

        await ApiClient({
          method: 'post',
          url: '/users/checkToken',
          headers: {
            Authorization: authorizationHeader
          }
        })

        this.clearAllStores()
        this.saveToken(spaceToken, '')

        query.delete(spaceTokenName)

        const cleanedQuery = urlSearchParamsToQueryRecord(query)
        const path = window.location.pathname
        const hash = window.location.hash || ''

        // Update the address bar immediately so we don't re-run checkToken; avoid awaiting router.replace here —
        // doing that during tokenCheckGuard (via isLoggedIn) nests navigation and can abort the route (blank page).
        const newSearch = query.toString()
        const newUrl = path + (newSearch ? `?${newSearch}` : '') + hash
        window.history.replaceState(window.history.state, '', newUrl)

        const router = (await import('@/router')).default
        Vue.nextTick(() => {
          router.replace({ path, query: cleanedQuery, hash: hash || undefined }).catch(() => {})
        })
      })()

      try {
        await checkSpaceTokenInFlight
      } finally {
        checkSpaceTokenInFlight = null
      }
    },
    async registerVerification(verificationToken: string): Promise<string> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/register-verification',
        data: {
          verificationToken
        }
      })
      const token = response.data['token']
      const refreshToken = response.data['refreshToken']
      if (token && refreshToken) {
        this.saveToken(token, refreshToken)
        return response.headers['userid']
      } else {
        throw new Error('Something went wrong with the Login')
      }
    },
    async sendGoogleRegistration(googleToken: string): Promise<void> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/register-with-google',
        data: { token: googleToken }
      })
      const token = response.data.token
      const refreshToken = response.data.refreshToken
      if (token && refreshToken) {
        this.saveToken(token, refreshToken)
        return
      } else {
        throw new Error('Something went wrong with the Google Registration')
      }
    },
    async sendInviteRegistration(registerRequest: RegisterRequest): Promise<User> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/registerFromInvite',
        data: {
          password: registerRequest.password,
          firstName: registerRequest.firstName?.trim() || undefined,
          lastName: registerRequest.lastName?.trim() || undefined,
          inviteToken: registerRequest.inviteToken
        }
      })
      return response.data as User
    },
    async sendRegistration(registerRequest: RegisterRequest): Promise<RegisterResponse> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/register',
        data: registerRequest
      })
      return response.data as RegisterResponse
    },
    async resendVerificationMail(): Promise<void> {
      await ApiClient({
        method: 'post',
        url: '/users/resend-verification'
      })
    },
    async validateInviteRegisterToken(token: String): Promise<User> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/validateRegisterFromInvite',
        data: {
          token
        }
      })
      return response.data as User
    },
    async resendPendingInvitation(email: string): Promise<void> {
      await ApiClient({
        method: 'post',
        url: '/users/resend-pending-invitation',
        data: {
          email
        }
      })
    },
    async sendPasswordResetEmail(email: string): Promise<void> {
      await ApiClient({
        method: 'post',
        url: '/users/sendPasswordResetEmail',
        data: {
          email
        }
      })
    },
    async validatePasswordResetToken(token: String): Promise<User> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/validatePasswordReset',
        data: {
          token
        }
      })
      return response.data as User
    },
    async updateUserPassword(token: String, newPassword: String): Promise<User> {
      const response = await ApiClient({
        method: 'post',
        url: '/users/changePassword',
        data: {
          token,
          newPassword
        }
      })
      return response.data as User
    },
    /** @returns whether a usable access token is in storage afterwards. See {@link refreshToken}. */
    async startTokenRefresh(): Promise<boolean> {
      if (refreshTokenInFlight) {
        return refreshTokenInFlight
      }

      refreshTokenInFlight = this.refreshToken().finally(() => {
        refreshTokenInFlight = null
      })
      this.refreshTokenPromise = refreshTokenInFlight
      return refreshTokenInFlight
    },

    async login(loginCall: () => Promise<string | void>): Promise<string | void> {
      this.startLogin()
      try {
        await loginCall()
        await useUserStore().fetchCurrentUser()

        const redirectPath = sanitizeRedirectPath(this.getEffectiveRedirectPath())
        if (redirectPath) {
          this.setRedirectPath(null)
          return redirectPath
        }
        if (useUserStore().isUserNotSetup) {
          return '/setup'
        }
        return '/recently-viewed'
      } finally {
        this.endLogin()
      }
    },
    async loginWithPassword(email: string, password: string): Promise<string | void> {
      const result = await this.login(async () => {
        const response = await ApiClient({
          method: 'post',
          url: '/login',
          data: { email, password }
        })
        const token = response.headers['token']
        const refreshToken = response.headers['refreshtoken']

        if (!token || !refreshToken) {
          throw new Error('Something went wrong with the Login')
        }
        await this.saveToken(token, refreshToken)
      })
      const currentUser = useUserStore().currentUser
      if (currentUser) {
        await trackLogin('email_password', currentUser.email, currentUser.id)
      }
      return result
    },
    async loginWithGoogle(googleToken: string): Promise<string | void> {
      const result = await this.login(async () => {
        const response = await ApiClient({
          method: 'post',
          url: '/users/login-with-google',
          data: { token: googleToken }
        })
        const token = response.data.token
        const refreshToken = response.data.refreshToken
        if (!token || !refreshToken) {
          throw new Error('Something went wrong with the Google Login')
        }
        await this.saveToken(token, refreshToken)
      })
      const currentUser = useUserStore().currentUser
      if (currentUser) {
        await trackLogin('google', currentUser.email, currentUser.id)
      }
      return result
    },
    async loginAsAnonymous(spaceId: string) {
      try {
        await this.login(async () => {
          const response = await ApiClient({
            method: 'post',
            url: `/users/login-as-anonymous`,
            params: {
              spaceId,
              userId: tokenStorage.anonymousUserId
            }
          })
          const token = response.data.token
          const refreshToken = response.data.refreshToken
          if (token && refreshToken) {
            this.savePublicToken(token, refreshToken)
          } else {
            throw new Error('Something went wrong with the Anonymous Login')
          }
        })
        return true
      } catch (e) {
        return false
      }
    },
    async initUser() {
      if (useUserStore().currentUser) {
        return
      }
      if (initUserInFlight) {
        await initUserInFlight
        return
      }

      initUserInFlight = (async () => {
        try {
          this.startLogin()
          await useUserStore()
            .fetchCurrentUser()
            .finally(() => {
              this.endLogin()
            })
        } finally {
          initUserInFlight = null
        }
      })()

      await initUserInFlight
    },
    async logout(emailPrefill: string | undefined = undefined, reason?: 'idle') {
      if (logoutInFlight) {
        return logoutInFlight
      }
      if (this.logoutLoading) {
        return
      }
      logoutInFlight = this.runLogout(emailPrefill, reason).finally(() => {
        logoutInFlight = null
      })
      return logoutInFlight
    },
    async runLogout(emailPrefill: string | undefined, reason?: 'idle') {
      this.logoutLoading = true
      const currentUser = useUserStore().currentUser
      const prefillEmail = emailPrefill ?? currentUser?.email

      const query: Record<string, string> = {}
      if (prefillEmail) {
        query.email = prefillEmail
      }
      if (reason) {
        query.reason = reason
      }
      const loginPath = Object.keys(query).length > 0 ? { path: '/login', query } : '/login'
      const loginHref = Object.keys(query).length > 0 ? `/login?${new URLSearchParams(query).toString()}` : '/login'

      // Track while tokens still exist. Fire-and-forget + clear raced and caused
      // /tracking/event → 401 → logout → trackLogout loops.
      if (currentUser) {
        try {
          await trackLogout()
        } catch {
          // Tracking must not block logout.
        }
      }

      // Drop auth immediately so navigation guards treat the user as logged out
      // and in-flight token refresh cannot restore the session mid-logout.
      this.setRedirectPath(null)
      try {
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY)
      } catch {
        // localStorage may be unavailable in some environments.
      }
      // NS-205: the trial-ended explainer is once per *login*, so its "seen" markers die with the
      // session they belong to. Dynamic import keeps the soft-lock module off the auth boot path.
      try {
        const { clearTrialEndedOnboardingSeen } = await import('@/modules/featurerestriction/trialSoftLock')
        clearTrialEndedOnboardingSeen()
      } catch {
        // Soft-lock cleanup must not block logout.
      }
      try {
        useWebSocketStore().disconnectWebSocket()
      } catch {
        // Tearing down a half-open socket must not block logout. Every step before clearAllStores()
        // is best-effort: logoutLoading stays latched from here on, so a throw that skipped the
        // token wipe would leave the tab unable to either use or end the session.
      }

      // Clear tokens/localStorage + pinia before navigating. $reset() also clears
      // logoutLoading, so restore it afterwards.
      this.clearAllStores()
      // Rendered document content must not outlive the session (never throws)
      clearStaticRenderCache()
      // Stays latched for the rest of this dead session — storing a new credential is what clears it
      // (see saveToken). Resetting it once navigation settled reopened the 401 retry path for every
      // request still queued from the logged-in view.
      this.logoutLoading = true

      const router = (await import('@/router')).default
      try {
        await router.replace(loginPath)
      } catch {
        window.location.assign(loginHref)
      }
    },
    /**
     * A logout the user asked for, or the idle timeout: changes still being saved get up to 2 s to reach the server
     * first, while the session is still valid. A logout because the session is dead (refresh failed) calls `logout`
     * directly: its saves could not succeed.
     */
    async logoutAfterSaving(emailPrefill: string | undefined = undefined, reason?: 'idle') {
      if (!logoutInFlight && !this.logoutLoading) {
        try {
          // Dynamic: the save coordinator imports this store
          const { flushPendingSaves } = await import('@/modules/elements/stores/SaveCoordinatorStore')
          await flushPendingSaves()
        } catch {
          // Saving must not block logout
        }
      }
      return this.logout(emailPrefill, reason)
    },
    async forceLogoutDueToIdle() {
      await this.logoutAfterSaving(undefined, 'idle')
    },
    clearAllStores() {
      interface ExtendedPinia extends Pinia {
        _s: Map<string, Store>
      }

      const pinia = getActivePinia() as ExtendedPinia

      clearStoresHooks.forEach(hook => {
        try {
          hook()
        } catch (error) {
          console.warn('[AuthStore] clear-stores hook failed', error)
        }
      })
      if (pinia) {
        pinia._s.forEach(store => {
          store.$reset()
        })
      }
      this.clear()
    }
  }
})

export type AuthStore = ReturnType<typeof useAuthStore>
